
import { getUserProfile } from '@/contexts/auth/services/profileService';
import { TEST_USER, TEST_PARTICIPANT } from '../constants';
import { User } from '../types';
import { jwtDecode } from "jwt-decode";
import { setTokens } from '@/services/apiService';
import { UserRoles } from '@/contexts/auth/enums';

interface JwtPayload {
  roles?: string[];
  username?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

// Helper function to map API roles to our UserRoles enum
const mapRoleFromJwt = (roles: string[]): UserRoles[] => {
  const mappedRoles: UserRoles[] = [];

  if (roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN')) {
    mappedRoles.push(UserRoles.ADMIN);
  } else if (roles.includes('ROLE_ORGANIZER') || roles.includes('ROLE_MEDIHUB_ORGANIZER')) {
    mappedRoles.push(UserRoles.ORGANIZER);
  } else {
    mappedRoles.push(UserRoles.USER);
  }

  return mappedRoles;
};

export const loginUser = async (email: string, password: string): Promise<{ success: boolean; token?: string; user?: User; message?: string }> => {


  const API_URL = import.meta.env.VITE_APP_CONGNESS_API_URL as string;

  try {
    console.log('Attempting login with:', { email });

    // Hozzáadunk egy késleltetést a tesztkörnyezetben a hálózati kérés szimuálásához
    // Ez segít elkerülni a gyors második kattintás miatti nem várt viselkedést
    if (!API_URL || API_URL.includes('localhost') || API_URL.includes('test')) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    const response = await fetch(`${API_URL}/auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    // Ellenőrizzük van-e válasz a szervertől
    if (!response) {
      return {
        success: false,
        message: "Kapcsolati hiba történt. Kérjük, próbálja újra később."
      };
    }

    const data = await response.json();
    console.log('Auth response:', data);

    if (!response.ok) {
      // Check if email is not verified
      if (data.code === 'email_not_verified') {
        return {
          success: false,
          message: "Az e-mail cím nincs megerősítve. Kérjük, ellenőrizze a postafiókját a megerősítő linkért."
        };
      }

      return {
        success: false,
        message: data.message || "Sikertelen bejelentkezés. Kérjük, ellenőrizze adatait."
      };
    }

    // Store both access token and refresh token
    setTokens(data.token, data.refresh_token);

    // Extract role from JWT token
    let userRoles: UserRoles[] = [UserRoles.USER]; // Default role

    try {
      const decoded = jwtDecode<JwtPayload>(data.token);
      console.log('JWT decoded:', decoded);
      if (decoded.roles && Array.isArray(decoded.roles)) {
        userRoles = mapRoleFromJwt(decoded.roles);
        console.log('Mapped role:', userRoles);
      }
    } catch (jwtError) {
      console.error('Error decoding JWT token:', jwtError);
    }

    // Get user profile with token
    const profileResult = await getUserProfile(data.token);
    console.log('Profile result:', profileResult);

    if (!profileResult.success || !profileResult.user) {
      console.error('Failed to get user profile:', profileResult);
      return {
        success: false,
        message: "Nem sikerült lekérni a felhasználói profilt. Kérjük, próbálja újra később."
      };
    }

    // Create user object with role from JWT and profile data
    const user: User = {
      ...profileResult.user,
      roles: userRoles,
    };

    console.log('Final user object:', user);

    return {
      success: true,
      token: data.token,
      user
    };
  } catch (error) {
    console.error('Login error:', error);
    return {
      success: false,
      message: "Kapcsolati hiba történt. Kérjük, próbálja újra később."
    };
  }
};
